import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Check, AlertTriangle, Sparkles, Loader2, History, X, SlidersHorizontal, Wallet, Lock } from "lucide-react";

export default function ImportStatement() {
  const { companyId, currency, categories } = useApp();
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [useAi, setUseAi] = useState(true);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mapping, setMapping] = useState(null);
  const [showMapping, setShowMapping] = useState(false);
  const [setOpening, setSetOpening] = useState(false);
  const [pwFile, setPwFile] = useState(null);
  const [pwValue, setPwValue] = useState("");
  const [pwError, setPwError] = useState("");

  const MAP_FIELDS = [
    ["date", "Date"], ["description", "Description"], ["debit", "Debit"],
    ["credit", "Credit"], ["amount", "Amount"], ["balance", "Balance"], ["reference", "Reference"],
  ];

  const loadHistory = () => api.get(`/companies/${companyId}/imports`).then(({ data }) => setHistory(data)).catch(() => {});
  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/accounts`).then(({ data }) => setAccounts(data)).catch(() => {});
    loadHistory();
  }, [companyId]);

  const analyze = async (f, overrideMapping, pw) => {
    const theFile = f || file;
    if (!theFile) return;
    if (f) setFile(f);
    setAnalyzing(true);
    setResult(null);
    const fd = new FormData();
    fd.append("file", theFile);
    fd.append("use_ai", useAi ? "true" : "false");
    if (accountId) fd.append("account_id", accountId);
    if (overrideMapping) fd.append("mapping", JSON.stringify(overrideMapping));
    if (pw) fd.append("password", pw);
    try {
      const { data } = await api.post(`/companies/${companyId}/import/analyze`, fd);
      if (data.needs_password) {
        setPwFile(theFile);
        setPwError(pw ? "Incorrect password — please try again." : "");
        return;
      }
      setPwFile(null); setPwValue(""); setPwError("");
      setResult(data);
      setMapping(data.mapping);
      setSetOpening(false);
      if (data.transactions.length) {
        toast.success(`Parsed ${data.transactions.length} transactions${data.remembered ? " · using saved column layout" : ""}`);
      } else {
        toast.info(data.errors?.join("; ") || "No transactions detected");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not parse file");
    } finally {
      setAnalyzing(false);
    }
  };

  const submitPassword = () => {
    if (analyzing || !pwValue.trim() || !pwFile) return;
    analyze(pwFile, null, pwValue.trim());
  };
  const cancelPassword = () => { setPwFile(null); setPwValue(""); setPwError(""); setFile(null); };

  const updateRow = (i, k, v) => {
    const rows = [...result.transactions];
    rows[i] = { ...rows[i], [k]: v };
    setResult({ ...result, transactions: rows });
  };
  const removeRow = (i) => {
    const rows = result.transactions.filter((_, idx) => idx !== i);
    setResult({ ...result, transactions: rows });
  };

  const confirm = async () => {
    const toSave = result.transactions.filter((t) => !t._skip);
    setSaving(true);
    try {
      const payload = { transactions: toSave };
      if (accountId) payload.account_id = accountId;
      if (setOpening && result.suggested_opening != null) payload.opening_balance = result.suggested_opening;
      const { data } = await api.post(`/companies/${companyId}/import/${result.import_id}/confirm`, payload);
      toast.success(`Imported ${data.imported} transactions`);
      setResult(null); setFile(null);
      loadHistory();
    } catch (e) { toast.error("Import failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="import-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-extrabold text-slate-900">Import Statement</h1>
        <p className="text-slate-500 text-sm mt-0.5">Upload an Excel, CSV or PDF bank statement. We'll parse, categorize and dedupe.</p>
      </div>

      {!result && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-xs font-semibold text-slate-600 mb-1">Import into account</label>
              <select data-testid="import-account" value={accountId} onChange={(e) => setAccountId(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none">
                <option value="">Select account (optional)</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-50 cursor-pointer" data-testid="ai-toggle">
                <input type="checkbox" checked={useAi} onChange={(e) => setUseAi(e.target.checked)} className="accent-indigo-600" />
                <Sparkles size={16} className="text-indigo-600" />
                <span className="text-sm font-semibold text-indigo-700">AI categorization</span>
              </label>
            </div>
          </div>

          <label data-testid="dropzone"
            className="block border-2 border-dashed border-slate-300 rounded-2xl p-10 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/30 transition-colors">
            <input type="file" accept="*/*" className="hidden" data-testid="file-input"
              onChange={(e) => analyze(e.target.files[0])} />
            {analyzing ? (
              <div className="flex flex-col items-center gap-3 text-indigo-600">
                <Loader2 size={40} className="animate-spin" />
                <p className="font-semibold">Analyzing {file?.name}...</p>
                {useAi && <p className="text-sm text-slate-400">AI is categorizing your transactions</p>}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <div className="w-14 h-14 rounded-2xl bg-indigo-100 grid place-items-center"><UploadCloud size={28} className="text-indigo-600" /></div>
                <p className="font-semibold text-slate-700">Drag & drop or click to upload</p>
                <p className="text-sm text-slate-400">Excel, CSV & PDF bank statements auto-import (password-protected PDFs supported); other files are stored for reference</p>
              </div>
            )}
          </label>
        </div>
      )}

      {pwFile && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="pdf-password-modal">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4 animate-fade-up">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-indigo-100 grid place-items-center"><Lock size={20} className="text-indigo-600" /></div>
              <div>
                <h3 className="font-heading font-bold text-slate-800">Password required</h3>
                <p className="text-xs text-slate-500 truncate max-w-[220px]">{pwFile.name}</p>
              </div>
            </div>
            <p className="text-sm text-slate-500">This PDF is password-protected. Enter the password to unlock and import it.</p>
            <input type="password" autoFocus data-testid="pdf-password-input" value={pwValue}
              onChange={(e) => setPwValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitPassword(); }}
              placeholder="Document password"
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-indigo-500/20" />
            {pwError && <p className="text-sm text-rose-600 flex items-center gap-1.5" data-testid="pdf-password-error"><AlertTriangle size={14} /> {pwError}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={cancelPassword} data-testid="pdf-password-cancel" className="px-4 py-2 rounded-xl border border-slate-200 font-semibold text-sm text-slate-600">Cancel</button>
              <button onClick={submitPassword} disabled={analyzing || !pwValue.trim()} data-testid="pdf-password-submit"
                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                {analyzing ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />} Unlock &amp; import
              </button>
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <FileSpreadsheet size={20} className="text-indigo-600" />
              <div>
                <p className="font-semibold text-slate-800">{result.transactions.length} transactions to review</p>
                <p className="text-xs text-slate-400">
                  Detected columns: {Object.entries(result.mapping).filter(([, v]) => v).map(([k]) => k).join(", ")}
                  {result.duplicates > 0 && <span className="text-amber-600 font-semibold"> · {result.duplicates} possible duplicates</span>}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setResult(null); setFile(null); }} data-testid="cancel-import-btn" className="px-4 py-2 rounded-xl border border-slate-200 font-semibold text-sm text-slate-600">Cancel</button>
              <button data-testid="confirm-import-btn" onClick={confirm} disabled={saving}
                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-60">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Confirm & Save
              </button>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="px-4 py-2 bg-amber-50 text-amber-700 text-sm flex items-center gap-2">
              <AlertTriangle size={15} /> {result.errors.join("; ")}
            </div>
          )}

          {(result.remembered || result.suggested_opening != null) && (
            <div className="px-4 py-2.5 flex flex-wrap items-center gap-3 border-b border-slate-100 bg-slate-50/60">
              {result.remembered && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-lg" data-testid="remembered-badge">
                  <SlidersHorizontal size={12} /> Using saved column layout
                </span>
              )}
              {result.suggested_opening != null && accountId && (
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="set-opening-toggle">
                  <input type="checkbox" checked={setOpening} onChange={(e) => setSetOpening(e.target.checked)} className="accent-indigo-600" />
                  <Wallet size={15} className="text-indigo-600" />
                  Set account opening balance to <b>{formatMoney(result.suggested_opening, currency)}</b>
                  {result.suggested_closing != null && <span className="text-slate-400">(closing {formatMoney(result.suggested_closing, currency)})</span>}
                </label>
              )}
              {result.suggested_opening != null && !accountId && (
                <span className="text-xs text-slate-400">Opening balance {formatMoney(result.suggested_opening, currency)} detected — pick an account above to apply it.</span>
              )}
            </div>
          )}

          <div className="px-4 py-2.5 border-b border-slate-100">
            <button data-testid="toggle-mapping-btn" onClick={() => setShowMapping((s) => !s)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-indigo-600">
              <SlidersHorizontal size={14} /> Column mapping {showMapping ? "▾" : "▸"}
            </button>
            {showMapping && mapping && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mapping-editor">
                {MAP_FIELDS.map(([field, label]) => (
                  <div key={field}>
                    <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
                    <select data-testid={`map-${field}`} value={mapping[field] || ""} onChange={(e) => setMapping({ ...mapping, [field]: e.target.value || null })}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs bg-white outline-none">
                      <option value="">— none —</option>
                      {result.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                ))}
                <div className="col-span-2 md:col-span-4 flex justify-end">
                  <button data-testid="apply-mapping-btn" onClick={() => analyze(null, mapping)}
                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 text-white font-semibold text-sm">
                    Apply mapping &amp; re-parse
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="overflow-x-auto max-h-[520px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Date</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Description</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Type</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Category</th>
                  <th className="text-right px-4 py-2.5 font-semibold">Amount</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {result.transactions.map((t, i) => (
                  <tr key={i} data-testid={`review-row-${i}`} className={`border-b border-slate-50 ${t.duplicate ? "bg-amber-50/50" : ""}`}>
                    <td className="px-4 py-2"><input value={t.date} onChange={(e) => updateRow(i, "date", e.target.value)} className="w-28 px-2 py-1 rounded-lg border border-slate-200 text-xs" /></td>
                    <td className="px-4 py-2"><input value={t.description} onChange={(e) => updateRow(i, "description", e.target.value)} className="w-full min-w-[180px] px-2 py-1 rounded-lg border border-slate-200 text-xs" /></td>
                    <td className="px-4 py-2">
                      <select value={t.type} onChange={(e) => updateRow(i, "type", e.target.value)} className={`px-2 py-1 rounded-lg border border-slate-200 text-xs font-semibold ${t.type === "credit" ? "text-emerald-600" : "text-rose-600"}`}>
                        <option value="credit">In</option><option value="debit">Out</option>
                      </select>
                    </td>
                    <td className="px-4 py-2">
                      <select value={t.category} onChange={(e) => updateRow(i, "category", e.target.value)} className="px-2 py-1 rounded-lg border border-slate-200 text-xs max-w-[150px]">
                        {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-right"><input type="number" value={t.amount} onChange={(e) => updateRow(i, "amount", parseFloat(e.target.value) || 0)} className="w-24 px-2 py-1 rounded-lg border border-slate-200 text-xs text-right" /></td>
                    <td className="px-4 py-2 text-right">
                      {t.duplicate && <span className="text-[10px] font-bold text-amber-600 mr-1">DUP</span>}
                      <button onClick={() => removeRow(i)} className="p-1 text-slate-300 hover:text-rose-500"><X size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Import history */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-4"><History size={18} className="text-indigo-600" /><h3 className="font-heading font-bold text-slate-800">Import History</h3></div>
        <div className="space-y-2">
          {history.length ? history.map((h) => (
            <div key={h.id} data-testid={`import-history-${h.id}`} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
              <div className="flex items-center gap-3">
                <FileSpreadsheet size={18} className="text-slate-400" />
                <div>
                  <p className="text-sm font-semibold text-slate-800">{h.filename}</p>
                  <p className="text-xs text-slate-400">{formatDate(h.created_at)} · {h.row_count} rows · by {h.created_by}</p>
                </div>
              </div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${h.status === "completed" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                {h.status === "completed" ? `Imported ${h.imported_count || 0}` : "Pending"}
              </span>
            </div>
          )) : <p className="text-sm text-slate-400 py-4 text-center">No imports yet</p>}
        </div>
      </div>
    </div>
  );
}
